export const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const MOBILE_CHART_HEIGHT = 600;
export const MOBILE_BREAKPOINT = 768;
export const TOUR_STORAGE_KEY = 'costingSummary_tourSeen';
export const SCROLL_DELAY_MS = 100;
export const TABLE_ROW_HEIGHT = 36;

export const INITIAL_FILTERS = {
    sites: [],
    providers: [],
    roles: [],
    weeks: [],
    periods: []
};

export const COLOUR_PALETTE = [
    'rgb(17, 141, 255)',
    'rgb(230, 108, 55)',
    'rgb(18, 35, 158)',
    'rgb(76, 175, 80)',
    'rgb(156, 39, 176)',
    'rgb(255, 193, 7)',
    'rgb(0, 150, 136)',
    'rgb(233, 30, 99)',
    'rgb(121, 85, 72)',
    'rgb(96, 125, 139)',
    'rgb(255, 87, 34)',
    'rgb(63, 81, 181)',
    'rgb(0, 188, 212)',
    'rgb(139, 195, 74)',
    'rgb(255, 152, 0)',
    'rgb(103, 58, 183)',
    'rgb(244, 67, 54)',
    'rgb(33, 150, 243)',
    'rgb(205, 220, 57)',
    'rgb(158, 158, 158)',
];

export const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const formatWeekDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

export const formatCurrencyFull = (value) => {
    if (value === null || value === undefined) return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `£${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatCurrencyCompact = (value, { emptyDisplay = '', forceFullBelow = 1000 } = {}) => {
    if (!value || value === 0) return emptyDisplay;
    if (Math.abs(value) < forceFullBelow) {
        return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `£${(value / 1000).toFixed(1)}k`;
};

export const formatHours = (value) => {
    if (value === null || value === undefined) return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const calculateDayTotals = (rows) => {
    const totals = {};
    DAY_ORDER.forEach(day => { totals[day] = 0; });
    totals.total = 0;
    rows.forEach(row => {
        const day = row.jobDay;
        const nett = parseFloat(row.nett) || 0;
        if (day && totals.hasOwnProperty(day)) {
            totals[day] += nett;
        }
        totals.total += nett;
    });
    return totals;
};

export const buildHierarchy = (data) => {
    if (!data || data.length === 0) return [];
    const byWeek = {};
    data.forEach(row => {
        const wk = row.weekEnding ? new Date(row.weekEnding).toISOString() : 'Unknown';
        if (!byWeek[wk]) byWeek[wk] = { weekEnding: row.weekEnding, weekKey: wk, rows: [] };
        byWeek[wk].rows.push(row);
    });
    return Object.values(byWeek)
        .sort((a, b) => new Date(b.weekEnding || 0) - new Date(a.weekEnding || 0))
        .map(week => {
            const bySite = {};
            week.rows.forEach(row => {
                const site = row.site_Name || 'Unknown Site';
                if (!bySite[site]) bySite[site] = { siteName: site, rows: [] };
                bySite[site].rows.push(row);
            });
            const sites = Object.values(bySite)
                .sort((a, b) => a.siteName.localeCompare(b.siteName))
                .map(site => {
                    const byRole = {};
                    site.rows.forEach(row => {
                        const role = row.roleName || 'Other';
                        if (!byRole[role]) byRole[role] = { roleName: role, rows: [] };
                        byRole[role].rows.push(row);
                    });
                    return {
                        siteName: site.siteName,
                        roles: Object.values(byRole).sort((a, b) => a.roleName.localeCompare(b.roleName)),
                        rows: site.rows
                    };
                });
            return { weekEnding: week.weekEnding, weekKey: week.weekKey, sites, rows: week.rows };
        });
};

export const filterDetailRows = (data, leftSelection) => {
    if (!data || data.length === 0 || !leftSelection) return [];
    if (leftSelection.type === 'all') return data;

    const matchWeek = (row) => {
        if (!leftSelection.weekEnding) return true;
        const selWeek = new Date(leftSelection.weekEnding).toDateString();
        const rowWeek = row.weekEnding ? new Date(row.weekEnding).toDateString() : null;
        return rowWeek === selWeek;
    };

    return data.filter(row => {
        switch (leftSelection.type) {
            case 'week':
                return matchWeek(row);
            case 'weekDay':
                return matchWeek(row) && row.jobDay === leftSelection.day;
            case 'weekRole':
                return matchWeek(row) && row.roleName === leftSelection.roleName;
            case 'site':
                return matchWeek(row) && row.site_Name === leftSelection.siteName;
            case 'siteDay':
                return matchWeek(row) && row.site_Name === leftSelection.siteName && row.jobDay === leftSelection.day;
            case 'roleDept':
                return matchWeek(row) && row.site_Name === leftSelection.siteName && row.roleName === leftSelection.roleName;
            case 'roleDeptDay':
                return matchWeek(row) && row.site_Name === leftSelection.siteName && row.roleName === leftSelection.roleName && row.jobDay === leftSelection.day;
            case 'columnDay':
                return row.jobDay === leftSelection.day;
            default:
                return true;
        }
    });
};
